import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTelemetry } from '../hooks/useTelemetry';
import { thingsboardService } from '../services/thingsboard';
import {
  XIcon,
  PlayIcon,
  PauseIcon,
  PowerIcon,
  LightbulbIcon,
  AlignJustifyIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  RotateCcwIcon,
  FileTextIcon,
  SettingsIcon,
  MoonIcon,
  ActivityIcon } from
'lucide-react';
import { useTranslation } from 'react-i18next';

type DeviceState = 'On (상시전원)' | 'On' | 'Off' | 'Open' | 'Close' | 'NoOp';
interface Stage {
  id: number;
  nameEn: string;
  nameKo: string;
  desc: string;
  devices: {
    SEO_7: DeviceState;
    SEO_30: DeviceState;
    SES_4: DeviceState;
    SEC_2: DeviceState;
  };
}
const STAGES: Stage[] = [
{
  id: 1,
  nameEn: 'IDLE',
  nameKo: '대기',
  desc: '체험자가 방, 침대, 미러의 센서 감지영역에 없는 상태입니다.',
  devices: {
    SEO_7: 'On (상시전원)',
    SEO_30: 'Off',
    SES_4: 'Off',
    SEC_2: 'Open'
  }
},
{
  id: 2,
  nameEn: 'ENTER ROOM',
  nameKo: '방 진입',
  desc: '체험자가 방에 진입한 상태입니다.',
  devices: {
    SEO_7: 'On (상시전원)',
    SEO_30: 'Off',
    SES_4: 'On',
    SEC_2: 'Open'
  }
},
{
  id: 3,
  nameEn: 'ENTER BED',
  nameKo: '침대 진입',
  desc: '체험자가 침대에 진입한 상태입니다.',
  devices: {
    SEO_7: 'On (상시전원)',
    SEO_30: 'On',
    SES_4: 'Off',
    SEC_2: 'Close'
  }
},
{
  id: 4,
  nameEn: 'SLEEP_ENTER',
  nameKo: '수면 진입',
  desc: '체험자가 수면에 진입한 상태로 수면 진입시의 상황입니다.',
  devices: {
    SEO_7: 'On (상시전원)',
    SEO_30: 'Off',
    SES_4: 'Off',
    SEC_2: 'Close'
  }
},
{
  id: 5,
  nameEn: 'SLEEPING',
  nameKo: '수면 중 / 암전',
  desc: '체험자가 수면 중인 상태로 수면 중의 상황입니다.',
  devices: {
    SEO_7: 'On (상시전원)',
    SEO_30: 'Off',
    SES_4: 'Off',
    SEC_2: 'Close'
  }
},
{
  id: 6,
  nameEn: 'WAKE-UP',
  nameKo: '기상',
  desc: '체험자가 수면 후 기상한 상태로 침대위에서 기상을 준비하는 상황입니다.',
  devices: {
    SEO_7: 'On (상시전원)',
    SEO_30: 'On',
    SES_4: 'Off',
    SEC_2: 'Open'
  }
},
{
  id: 7,
  nameEn: 'BED-OUT',
  nameKo: '침대 이탈',
  desc: '체험자가 침대를 벗어나 기상 후 하루를 준비하는 상황입니다.',
  devices: {
    SEO_7: 'On (상시전원)',
    SEO_30: 'Off',
    SES_4: 'On',
    SEC_2: 'Open'
  }
}];

const getDeviceStatusColor = (state: DeviceState | string) => {
  if (state.includes('On')) return 'bg-amber-400/10 border-amber-400/30 text-amber-400';
  if (state === 'Open') return 'bg-sky-400/10 border-sky-400/30 text-sky-400';
  return 'bg-slate-800/50 border-white/5 text-slate-400';
};

const getDeviceIconColor = (state: DeviceState | string) => {
  if (state.includes('On')) return 'text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]';
  if (state === 'Open') return 'text-sky-400 drop-shadow-[0_0_8px_rgba(56,189,248,0.5)]';
  return 'text-slate-500';
};

function RealtimeDeviceStatus({
  sensor,
  type,
  defaultLabel,
  targetState,
  Icon
}: {
  sensor?: any;
  type: 'SEO' | 'SES' | 'SEC';
  defaultLabel: string;
  targetState: string;
  Icon: React.ElementType;
}) {
  const telemetryKeys = type === 'SEO' ? ['plug_on'] : type === 'SES' ? ['switch'] : type === 'SEC' ? ['curtain_state'] : [];
  const { data: realtimeData } = useTelemetry(sensor?.id || null, telemetryKeys);
  
  let currentStateStr = 'Off';
  
  if (sensor && type === 'SEO') {
    const plugOn = realtimeData?.['plug_on'];
    if (plugOn && plugOn.length > 0) {
      const val = plugOn[0].value;
      const isOn = String(val) === 'true' || String(val) === 'on';
      currentStateStr = isOn ? 'On' : 'Off';
    }
  } else if (sensor && type === 'SES') {
    const switchState = realtimeData?.['switch'];
    if (switchState && switchState.length > 0) {
      const val = switchState[0].value;
      const isOn = String(val) === 'true' || String(val) === 'on';
      currentStateStr = isOn ? 'On' : 'Off';
    }
  } else if (sensor && type === 'SEC') {
    const curtainState = realtimeData?.['curtain_state'];
    if (curtainState && curtainState.length > 0) {
      const val = curtainState[0].value;
      currentStateStr = String(val == '0' ? '완전열림' : val == '100' ? '완전닫힘' : '부분열림');
    }
  }

  // Restore '(상시전원)' trailing string if applicable to match original target string styles
  if (currentStateStr === 'On' && targetState.includes('상시전원')) {
    currentStateStr = 'On (상시전원)';
  }

  return (
    <div className={`flex flex-col p-4 rounded-xl border transition-colors ${getDeviceStatusColor(currentStateStr)}`}>
      <div className="flex items-center justify-between mb-3">
        <Icon className={`w-5 h-5 ${getDeviceIconColor(currentStateStr)}`} />
        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-black/20">
          {defaultLabel}
        </span>
      </div>
      <div className="text-sm font-bold mt-auto">
        {currentStateStr}
      </div>
    </div>
  );
}

interface SleepCareDemoProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenReport: () => void;
  sensors?: any[];
}
export function SleepCareDemo({
  isOpen,
  onClose,
  onOpenReport,
  sensors = []
}: SleepCareDemoProps) {
  const { t } = useTranslation();
  const [stageIndex, setStageIndex] = useState(0);
  const [isAuto, setIsAuto] = useState(false);

  const [delays, setDelays] = useState({ stage3: 1, stage4: 20, stage5: 30, stage6: 40 });
  const [showSettings, setShowSettings] = useState(false);
  const [tempDelays, setTempDelays] = useState(delays);

  useEffect(() => {
    const loadDelays = async () => {
      const assetId = localStorage.getItem('apartmentAssetId');
      if (assetId) {
        try {
          const res = await thingsboardService.getEntityAttributes('ASSET', assetId, 'SERVER_SCOPE', ['timeDelay']);
          if (res && res.length > 0 && res[0].key === 'timeDelay') {
            const parsed = typeof res[0].value === 'string' ? JSON.parse(res[0].value) : res[0].value;
            setDelays(prev => ({ ...prev, ...parsed }));
          }
        } catch (e) {
          console.warn('Failed to load timeDelay', e);
        }
      }
    };
    loadDelays();
  }, []);

  const saveDelays = async () => {
    setDelays(tempDelays);
    setShowSettings(false);
    const assetId = localStorage.getItem('apartmentAssetId');
    if (assetId) {
      try {
        await thingsboardService.saveEntityAttributes('ASSET', assetId, 'SERVER_SCOPE', {
          timeDelay: tempDelays
        });
      } catch (e) {
        console.error('Failed to save timeDelay', e);
      }
    }
  };

  const ceilingSensor = sensors.find((s: any) => s.label === 'SEL_4');
  const bedSensor = sensors.find((s: any) => s.label === 'M02F100007');

  const { data: ceilingData } = useTelemetry(ceilingSensor?.id || null, ['presence_state']);
  const { data: bedData } = useTelemetry(bedSensor?.id || null, ['isPresentFromDevice']);

  const getPresenceCeiling = (data: any) => {
    if (!data?.['presence_state']?.length) return 0;
    const val = data['presence_state'][0].value;
    return (val === 'yes') ? 1 : 0;
  };
  
  const getPresenceBed = (data: any) => {
    if (!data?.['isPresentFromDevice']?.length) return 0;
    const val = data['isPresentFromDevice'][0].value;
    return (val === 'true' || val == true) ? 1 : 0;
  };

  const cPresence = getPresenceCeiling(ceilingData);
  const bPresence = getPresenceBed(bedData);

  const bedActiveRef = useRef<number | null>(null);

  // Auto-advance logic based on sensors
  useEffect(() => {
    if (!isAuto) {
      bedActiveRef.current = null;
      return;
    }
    
    let interval: any;
    const evaluate = () => {
      if (cPresence === 0 && bPresence === 0) {
        setStageIndex(0);
        bedActiveRef.current = null;
      } else if (cPresence === 1 && bPresence === 0) {
        bedActiveRef.current = null;
        setStageIndex(prev => {
          if (prev === 0) return 1;
          if (prev === 5) return 6;
          return prev;
        });
      } else if (bPresence === 1) {
        if (bedActiveRef.current === null) {
          bedActiveRef.current = Date.now();
        }
        const elapsedSec = (Date.now() - bedActiveRef.current) / 1000;
        setStageIndex(prev => {
            if (elapsedSec >= delays.stage6) return 5;
            if (elapsedSec >= delays.stage5) return 4;
            if (elapsedSec >= delays.stage4) return 3;
            if (elapsedSec >= delays.stage3) return 2;
            return prev;
        });
      }
    };

    evaluate();
    
    if (bPresence === 1) {
       interval = setInterval(evaluate, 500);
    }
    return () => {
       if (interval) clearInterval(interval);
    };
  }, [isAuto, cPresence, bPresence, delays]);
  // Reset when opened
  useEffect(() => {
    if (isOpen) {
      setStageIndex(0);
      setIsAuto(false);
    }
  }, [isOpen]);

  const executedStageRef = useRef<number>(-1);

  // Execute RPC based on current stage
  useEffect(() => {
    if (!isOpen) {
      executedStageRef.current = -1;
      return;
    }
    if (executedStageRef.current === stageIndex) return;
    
    const stage = STAGES[stageIndex];
    if (!stage) return;

    executedStageRef.current = stageIndex;

    const devicesToToggle = [
      { id: 'SEO_7', type: 'SEO', state: stage.devices.SEO_7 },
      { id: 'SEO_30', type: 'SEO', state: stage.devices.SEO_30 },
      { id: 'SES_4', type: 'SES', state: stage.devices.SES_4 },
      { id: 'SEC_2', type: 'SEC', state: stage.devices.SEC_2 }
    ];

    const executeCommands = async () => {
      let hubSensor = sensors.find((s: any) => s.label === 'SEC_0');
      
      if (!hubSensor) {
        try {
          const device = await thingsboardService.getTenantDevice('SEC_0');
          if (device && device.id) {
            hubSensor = { id: device.id.id, label: device.name, type: device.type };
          }
        } catch (e) {
          console.warn('[SleepCare] Could not fetch SEC_0 hub via API', e);
        }
      }

      for (const dev of devicesToToggle) {
        const sensor = sensors.find((s: any) => s.label === dev.id);
        if (!sensor) continue;

        if (dev.type === 'SEC' && dev.state !== 'NoOp') {
          console.log(`[SleepCare] Processing SEC device: ${dev.id}`);
          if (!hubSensor) {
            console.warn(`[SleepCare] Hub sensor SEC_0 not found in sensors! Aborting SEC command.`);
            continue;
          }
          const match = dev.id.match(/SEC_0?(\d+)/);
          if (!match) {
            console.warn(`[SleepCare] Could not match dev.id to SEC format: ${dev.id}`);
            continue;
          }
          const devNo = parseInt(match[1], 10);
          
          // SEC state in the demo is 'Open' or 'Close'
          const action = dev.state === 'Open' ? 'open' : 'close';
          const commandParams = { value: action, dev_no: devNo };
          for (let i = 0; i < 2; i++) {
            try {
              console.log('Sending set_curtain command to hub in sleepcare:', hubSensor.id, 'with dev_no:', devNo, 'and action:', commandParams);
              await thingsboardService.sendOneWayRpc(hubSensor.id, 'set_curtain', commandParams);
              
              /*setTimeout(() => {
                console.log('Requesting get_curtain_state in sleepcare after command');
                thingsboardService.sendOneWayRpc(hubSensor.id, 'get_curtain_state', { value: 0, dev_no: devNo })
                  .catch(err => console.error('Failed to request get_curtain_state after sleepcare command', err));
              }, 40000);*/
            } catch (e) {
              console.error(`Failed to handle curtain ${dev.id}:`, e);
            }
            if (i < 2) await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } else {
          const method = dev.type === 'SEO' ? 'set_plug' : 'set_switch';
          const isOn = dev.state.includes('On');
          const commandParams = { value: isOn ? 'on' : 'off' };
          
          for (let i = 0; i < 2; i++) {
            try {
              console.log('Sending set_plug command to device in sleepcare:', sensor.label, 'and action:', commandParams);
              await thingsboardService.sendOneWayRpc(sensor.id, method, commandParams);
            } catch (e) {
              console.error(`Failed to toggle ${dev.id}:`, e);
            }
            /*setTimeout(() => {
              console.log('Sending set_plug command to device in sleepcare:', sensor.label, 'and action:', commandParams);
              thingsboardService.sendOneWayRpc(sensor.id, method, commandParams)
                  .catch(err => console.error('Failed to send set_plug after set_plug command', err));
            }, 200);*/
            
            if (i < 2) await new Promise(resolve => setTimeout(resolve, 200));
          }
        }

        // Wait 0.3s before sending the next command
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    };

    executeCommands();
  }, [stageIndex, isOpen, sensors]);
  if (!isOpen) return null;
  const currentStage = STAGES[stageIndex];
  
  const getSensor = (labelMatch: string) => sensors.find((s: any) => s.label === labelMatch);
  return (
    <AnimatePresence>
      <motion.div
        initial={{
          opacity: 0
        }}
        animate={{
          opacity: 1
        }}
        exit={{
          opacity: 0
        }}
        className="fixed inset-0 z-[250] flex items-center justify-center p-4 sm:p-6"
        onClick={onClose}>
        
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />

        {/* Modal Content */}
        <motion.div
          initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }}
          animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }}
          exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-5xl bg-slate-900/95 backdrop-blur-xl border border-indigo-500/30 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
          
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-gradient-to-r from-indigo-900/40 to-slate-900/40">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-500/20 text-indigo-400">
                <MoonIcon className="w-5 h-5" />
              </div>
              <h2 className="text-lg font-bold text-white tracking-wide">
                {t('sleep_care.demo_title')}
              </h2>
            </div>

            <div className="flex items-center gap-6">
              {/* Auto/Manual Toggle */}
              <div className="flex items-center gap-3 bg-slate-800/80 p-1.5 rounded-xl border border-white/5">
                <button
                  onClick={() => setIsAuto(false)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-all ${!isAuto ? 'bg-indigo-500 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'}`}>
                  
                  {t('sleep_care.manual')}
                </button>
                <button
                  onClick={() => setIsAuto(true)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-all flex items-center gap-1.5 ${isAuto ? 'bg-emerald-500 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'}`}>
                  
                  {isAuto ?
                  <PauseIcon className="w-4 h-4" /> :

                  <PlayIcon className="w-4 h-4" />
                  }
                  {t('sleep_care.auto')}
                </button>
              </div>

              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition-colors">
                
                <XIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-8">
            {/* Current Stage Info */}
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="flex-shrink-0 flex flex-col items-center justify-center w-24 h-24 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-violet-500/20 border border-indigo-500/30 shadow-inner">
                <span className="text-sm font-bold text-indigo-300 uppercase tracking-widest mb-1">
                  Step
                </span>
                <span className="text-4xl font-black text-white drop-shadow-md">
                  {currentStage.id}
                </span>
              </div>

              <div className="flex-1">
                <div className="flex items-baseline gap-3 mb-2">
                  <h3 className="text-3xl font-extrabold text-white tracking-tight">
                    {currentStage.nameEn}
                  </h3>
                  <span className="text-lg font-bold text-indigo-300">
                    ({t(`sleep_care.stage_idx_${currentStage.id}_name`)})
                  </span>
                </div>
                <div className="text-slate-300 leading-relaxed whitespace-pre-wrap bg-white/5 p-4 rounded-xl border border-white/5">
                  {t(`sleep_care.stage_idx_${currentStage.id}_desc`)}
                </div>
              </div>
            </div>

            {/* IoT Devices Status */}
            <div>
              <h4 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                {t('sleep_care.iot_status')}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {/* 천장재실센서 (SEL_4) */}
                <div className={`flex flex-col p-4 rounded-xl border transition-colors ${cPresence === 1 ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-slate-800/50 border-white/5 text-slate-400'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <ActivityIcon className={`w-5 h-5 ${cPresence === 1 ? 'text-emerald-400 drop-shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'text-slate-500'}`} />
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-black/20">
                      천장재실(SEL_4)
                    </span>
                  </div>
                  <div className="text-sm font-bold mt-auto">
                    {cPresence === 1 ? '재실' : '부재'}
                  </div>
                </div>

                {/* 침대재실센서 (M02F100007) */}
                <div className={`flex flex-col p-4 rounded-xl border transition-colors ${bPresence === 1 ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-slate-800/50 border-white/5 text-slate-400'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <ActivityIcon className={`w-5 h-5 ${bPresence === 1 ? 'text-emerald-400 drop-shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'text-slate-500'}`} />
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-black/20">
                      침대재실(M02F100007)
                    </span>
                  </div>
                  <div className="text-sm font-bold mt-auto">
                    {bPresence === 1 ? '재실' : '부재'}
                  </div>
                </div>

                {/* Consent 7 */}
                <RealtimeDeviceStatus
                  sensor={getSensor('SEO_7')}
                  type="SEO"
                  defaultLabel="침대·미러·스피커(SEO_7)"
                  targetState={currentStage.devices.SEO_7}
                  Icon={PowerIcon}
                />
                {/* Consent 30 */}
                <RealtimeDeviceStatus
                  sensor={getSensor('SEO_30')}
                  type="SEO"
                  defaultLabel="탁상조명(SEO_30)"
                  targetState={currentStage.devices.SEO_30}
                  Icon={PowerIcon}
                />
                {/* 천장조명 */}
                <RealtimeDeviceStatus
                  sensor={getSensor('SES_4')}
                  type="SES"
                  defaultLabel="천장조명(SES_4)"
                  targetState={currentStage.devices.SES_4}
                  Icon={LightbulbIcon}
                />
                {/* 전동블라인드 */}
                <RealtimeDeviceStatus
                  sensor={getSensor('SEC_2')}
                  type="SEC"
                  defaultLabel="전동블라인드(SEC_2)"
                  targetState={currentStage.devices.SEC_2}
                  Icon={AlignJustifyIcon}
                />
              </div>
            </div>

            {/* Stage Navigation Stepper */}
            <div>
              <h4 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                {t('sleep_care.scenario_step')}
              </h4>
              <div className="flex flex-wrap gap-2">
                {STAGES.map((stage, i) => {
                  const isCurrent = i === stageIndex;
                  const isPast = i < stageIndex;
                  let btnClass =
                  'flex-1 min-w-[80px] py-3 px-2 rounded-xl border flex flex-col items-center justify-center gap-1 transition-all ';
                  if (isCurrent) {
                    btnClass +=
                    'bg-indigo-600 border-indigo-400 text-white shadow-[0_0_15px_rgba(79,70,229,0.4)] scale-105 z-10';
                  } else if (isPast) {
                    btnClass +=
                    'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 cursor-pointer';
                  } else {
                    btnClass +=
                    'bg-slate-800/50 border-slate-700 text-slate-500 hover:bg-slate-800 hover:text-slate-300 cursor-pointer';
                  }
                  return (
                    <button
                      key={stage.id}
                      disabled={isCurrent}
                      onClick={() => {
                        setStageIndex(i);
                        setIsAuto(false); // Stop auto if manually clicked
                      }}
                      className={btnClass}>
                      
                      <span className="text-xs font-robotofont-bold opacity-80">
                        Step {stage.id}
                      </span>
                      <span className="text-[0.8rem] font-roboto font-bold text-center leading-tight">
                        {t(`sleep_care.stage_idx_${stage.id}_name`)}
                      </span>
                    </button>);

                })}
              </div>
            </div>
          </div>

          {/* Footer Controls */}
          <div className="p-6 border-t border-white/10 bg-slate-900/80 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setStageIndex(0);
                  setIsAuto(false);
                }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors border border-slate-700 font-bold text-sm">
                
                <RotateCcwIcon className="w-4 h-4" />
                {t('sleep_care.return_idle')}
              </button>

              <button
                onClick={() => {
                  onClose();
                  onOpenReport();
                }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30 transition-colors border border-indigo-500/30 font-bold text-sm ml-4">
                
                <FileTextIcon className="w-4 h-4" />
                {t('sleep_care.view_report')}
              </button>

              <button
                onClick={() => {
                  setTempDelays(delays);
                  setShowSettings(true);
                }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-700/50 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors border border-slate-600 font-bold text-sm ml-4">
                <SettingsIcon className="w-4 h-4" />
                시간간격설정
              </button>
            </div>

            <div className="flex items-center gap-3">
              <button
                disabled={stageIndex === 0}
                onClick={() => {
                  setStageIndex((prev) => prev - 1);
                  setIsAuto(false);
                }}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-bold text-sm">
                
                <ChevronLeftIcon className="w-4 h-4" />
                {t('sleep_care.prev')}
              </button>
              <button
                onClick={() => {
                  if (stageIndex >= STAGES.length - 1) {
                    setStageIndex(0);
                  } else {
                    setStageIndex((prev) => prev + 1);
                  }
                  setIsAuto(false);
                }}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-500 transition-colors font-bold text-sm shadow-lg shadow-indigo-500/20">
                
                {stageIndex >= STAGES.length - 1 ? t('sleep_care.return_idle') : t('sleep_care.next')}
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Settings Dialog */}
        {showSettings && (
          <div className="absolute inset-0 bg-black/60 z-[300] flex items-center justify-center backdrop-blur-sm rounded-2xl p-4">
            <div className="bg-slate-800 p-6 rounded-xl shadow-2xl border border-indigo-500/30 w-[400px] max-w-[95%] flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-white mb-2">시간 간격 설정 (초)</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-300 font-bold">3단계 진입 (침대 진입)</span>
                  <input type="number" min="0" value={tempDelays.stage3} onChange={e => setTempDelays({...tempDelays, stage3: Number(e.target.value)})} className="w-24 px-3 py-2 bg-slate-900 text-white border border-slate-600 rounded-lg text-center font-bold" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-300 font-bold">4단계 진입 (수면 진입)</span>
                  <input type="number" min="0" value={tempDelays.stage4} onChange={e => setTempDelays({...tempDelays, stage4: Number(e.target.value)})} className="w-24 px-3 py-2 bg-slate-900 text-white border border-slate-600 rounded-lg text-center font-bold" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-300 font-bold">5단계 진입 (수면 중/암전)</span>
                  <input type="number" min="0" value={tempDelays.stage5} onChange={e => setTempDelays({...tempDelays, stage5: Number(e.target.value)})} className="w-24 px-3 py-2 bg-slate-900 text-white border border-slate-600 rounded-lg text-center font-bold" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-300 font-bold">6단계 진입 (기상)</span>
                  <input type="number" min="0" value={tempDelays.stage6} onChange={e => setTempDelays({...tempDelays, stage6: Number(e.target.value)})} className="w-24 px-3 py-2 bg-slate-900 text-white border border-slate-600 rounded-lg text-center font-bold" />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-4">
                <button onClick={() => setShowSettings(false)} className="px-5 py-2.5 bg-slate-700 text-white font-bold rounded-xl hover:bg-slate-600 transition-colors">취소</button>
                <button onClick={saveDelays} className="px-5 py-2.5 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-500 transition-colors shadow-lg shadow-indigo-500/20">저장</button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>);

}